import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import moment from 'jalali-moment';
import { toast } from 'react-toastify';

import { _post } from '../../../utils/network';
import { decommafy, swal, toEnglishDigits, typeChecker } from '../../../utils/methods';

import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb.component';
import FormField from "../../../components/Form/FormField/FormField.component";
import LoadingButton from '../../../components/LoadingButton/LoadingButton.component';
import ListTable from '../../../components/List/ListTable/ListTable.component';
import Pagination from '../../../components/List/Pagination/Pagination.component';

const ContestBestsList = () => {
	const { t } = useTranslation();
	const breadcrumbItems = [
		{ title: t('dashboard'), link: '/dashboard' },
		{ title: t('the_bests'), link: '/contest-bests-list' },
		{ title: t('list') }
	];
	const confirmSwal = statements => {
		swal(() => statements(), {
			text: t('are_you_sure_to_do_this'),
			confirmButtonText: t('yes'),
			cancelButtonText: t('no')
		});
	};

	const [contestBests, setContestBests] = useState([]);
	let latestContestBestsListData = {
		head: [t('user'), t('frequency_of_participation'), t('average_efficiency'), t('received_prize')],
		body: contestBests.map(contestBest => [
			<Link className='' to={`/users-edit/${contestBest.user_id}`} target="_blank">
				{contestBest.user_id_information.user_fullname} ({contestBest.user_id_information.national_code})
			</Link>,
			contestBest.total_enrollments,
			contestBest.total_profit,
			contestBest.total_rewards
		]),
		directions: ['rtl', 'ltr', 'ltr', 'ltr']
	};
	
	const [pageNumber, setPageNumber] = useState(1);
	const [pageCounter] = useState(10);
	const [totalCount, setTotalCount] = useState(0);
	const [pagesCount, setPagesCount] = useState(0);
	const [searchContestBestStartDate, setSearchContestBestStartDate] = useState(null);
	const [searchContestBestEndDate, setSearchContestBestEndDate] = useState(null);
	const [awardingContestBestStartDate, setAwardingContestBestStartDate] = useState(null);
	const [awardingContestBestEndDate, setAwardingContestBestEndDate] = useState(null);
	const [searchButtonIsLoading, setSearchButtonIsLoading] = useState(false);
	const [awardingButtonIsLoading, setAwardingButtonIsLoading] = useState(false);
	const [searchCounter, setSearchCounter] = useState(0);
	const getContestBests = async () => {
		let body = {
			start_date: searchContestBestStartDate,
			end_date: searchContestBestEndDate,
			page: pageNumber,
			count: pageCounter
		};
		setSearchButtonIsLoading(true);
		let response = await _post('/users/admin/getAllCompetitors', body);
		setSearchButtonIsLoading(false);
		switch(response.code){
			case 200:
				setTotalCount(response.data.all_competitors);
				setContestBests(response.data.competitors);
				break;
			default:
				toast.error(response[t('api_message')]);
		}
	};
	
	const filterContestBests = event => {
		event.preventDefault();
		if(searchButtonIsLoading)
			return 0;
		let startDate = event.target.searchContestBestStartDate.value.trim() || null;
		if(!!startDate && !typeChecker(startDate, 'shamsi-date'))
			return toast.warn(t('enter_start_date_correctly'));
		setAwardingContestBestStartDate(startDate);
		startDate = startDate ? moment(startDate, 'jYYYY/jMM/jDD').locale('en').format('YYYY-MM-DD') : null;
		setSearchContestBestStartDate(startDate);
		let endDate = event.target.searchContestBestEndDate.value.trim() || null;
		if(!!endDate && !typeChecker(endDate, 'shamsi-date'))
			return toast.warn(t('enter_end_date_correctly'));
		setAwardingContestBestEndDate(endDate);
		endDate = endDate ? moment(endDate, 'jYYYY/jMM/jDD').locale('en').format('YYYY-MM-DD') : null;
		setSearchContestBestEndDate(endDate);
		if(pageNumber === 1)
			setSearchCounter(searchCounter + 1);
		else
			setPageNumber(1);
	};
	
	const awardingToContestBests = async event => {
		event.preventDefault();
		if(awardingButtonIsLoading)
			return 0;
		let startDate = event.target.awardingContestBestStartDate.value.trim() || null;
		if(!!startDate && !typeChecker(startDate, 'shamsi-date'))
			return toast.warn(t('enter_start_date_correctly'));
		startDate = startDate ? moment(startDate, 'jYYYY/jMM/jDD').locale('en').format('YYYY-MM-DD') : null;
		let endDate = event.target.awardingContestBestEndDate.value.trim() || null;
		if(!!endDate && !typeChecker(endDate, 'shamsi-date'))
			return toast.warn(t('enter_end_date_correctly'));
		endDate = endDate ? moment(endDate, 'jYYYY/jMM/jDD').locale('en').format('YYYY-MM-DD') : null;
		let startCounter = parseInt(toEnglishDigits(decommafy(event.target.awardingContestBestStartCounter.value.trim())));
		if(!typeChecker(startCounter, 'number') || (startCounter <= 0))
			return toast.warn(t('enter_start_counter_correctly'));
		let endCounter = parseInt(toEnglishDigits(decommafy(event.target.awardingContestBestEndCounter.value.trim())));
		if(!typeChecker(endCounter, 'number') || (endCounter <= startCounter))
			return toast.warn(t('enter_end_counter_correctly'));
		let amount = parseInt(toEnglishDigits(decommafy(event.target.awardingContestBestAmount.value.trim())));
		if(!typeChecker(amount, 'number'))
			return toast.warn(t('enter_amount_correctly'));
		let body = {
			start_date: startDate,
			end_date: endDate,
			start_index: startCounter,
			end_index: endCounter,
			transaction_amount: amount,
			transactioncategory_id: "ab45b259-84e0-4aa2-acb0-a79027804b97"
		};
		confirmSwal(async () => {
			setAwardingButtonIsLoading(true);
			let response = await _post('/transactions/admin/setRewards', body);
			setAwardingButtonIsLoading(false);
			switch(response.code){
				case 200:
					toast.success(response[t('api_message')]);
					break;
				default:
					toast.error(response[t('api_message')]);
			}
		});
	};

	useEffect(() => {
		setPagesCount(Math.ceil(totalCount / pageCounter));
	}, [totalCount]);
	
	useEffect(() => {
		setSearchCounter(searchCounter + 1);
	}, [pageNumber]);

	useEffect(() => {
		getContestBests();
	}, [searchCounter]);
	
	return (
		<>
			<Breadcrumb items={breadcrumbItems} />
			<div className="card mb-3">
				<div className="card-header">
					<h5 className="mb-0">{t('search_for')} {t('the_bests')}</h5>
				</div>
				<div className="card-body bg-light">
					<form onSubmit={filterContestBests}>
						<div className="row gx-2">
							<FormField fieldKey="searchContestBestStartDate" title={t('start_date')} value={searchContestBestStartDate} tip={'1401/09/13'} isSearch={true} ltr />
							<FormField fieldKey="searchContestBestEndDate" title={t('end_date')} value={searchContestBestEndDate} tip={'1401/09/13'} isSearch={true} ltr />
						</div>
						<div>
							<LoadingButton className="btn btn-primary d-inline-block mt-3 me-2" type="submit" name="create" isLoading={searchButtonIsLoading}>
								<i className="fa fa-search"></i> {t('search')}
							</LoadingButton>
						</div>
					</form>
				</div>
			</div>
			<ListTable title={t('the_bests')} pageNumber={pageNumber} pageCounter={pageCounter} totalCount={totalCount} data={latestContestBestsListData} />
			<Pagination pagesCount={pagesCount} action={setPageNumber} />
			<div className="card mb-3">
				<div className="card-header">
					<h5 className="mb-0">{t('awarding')}</h5>
				</div>
				<div className="card-body bg-light">
					<form onSubmit={awardingToContestBests}>
						<div className="row gx-2">
							<FormField fieldKey="awardingContestBestStartDate" title={t('start_date')} value={awardingContestBestStartDate} isSearch={true} ltr disabled />
							<FormField fieldKey="awardingContestBestEndDate" title={t('end_date')} value={awardingContestBestEndDate} isSearch={true} ltr disabled />
							<FormField fieldKey="awardingContestBestType" title={t('type')} value={t('score1_name')} isSearch={true} disabled />
							<FormField type="number" fieldKey="awardingContestBestStartCounter" title={t('start_counter')} isSearch={true} ltr required />
							<FormField type="number" fieldKey="awardingContestBestEndCounter" title={t('end_counter')} isSearch={true} ltr required />
							<FormField type="number" fieldKey="awardingContestBestAmount" title={t('amount')} isSearch={true} ltr required />
						</div>
						<div>
							<LoadingButton className="btn btn-primary d-inline-block mt-3 me-2" type="submit" name="create" isLoading={awardingButtonIsLoading}>
								{t('awarding')}
							</LoadingButton>
						</div>
					</form>
				</div>
			</div>
		</>
	);
};

export default ContestBestsList;
